/**
 * KORTENE I EN SAMTALE (QA13 15 og 47 punkt 1, KONTRAKT-B «B-send»).
 *
 * Et kort er en melding uten forfatter (`body_format = 'system'`) med JSON i kroppen, som
 * systemlinjene i delingstrådene (0462) — men med en egen nøkkel, `card`, så de to aldri forveksles:
 * en linje er en setning midt i samtalen, et kort er en ting med innhold og kanskje knapper.
 *
 * Kortene skrives bare av tjenestene (`ConversationCardsService`, gjennom `app.post_conversation_card`
 * i 0933), aldri av en klient. Basen skriver aldri en setning; nettleseren oversetter.
 *
 * - `broadcast_shifts`: vaktene til akkurat denne mottakeren, under beskjeden (44).
 * - `contact_request`: en kontaktforespørsel. Innholdet og Godta/Avslå kommer fra B-kon.
 * - `invitation`: en invitasjon. Innholdet kommer fra B-del.
 * - `booking_bid` og `booking_answer`: et bud eller en forespørsel, og svaret på det, i bookingtråden
 *   (257.16, analysen av 19). Før sto de som tekst skrevet i avsenderens navn; nå er de kort med
 *   beløp, dato og status, og en eventuell melding står som vanlig melding under.
 */
import { z } from 'zod';

export const CONVERSATION_CARD_KINDS = [
  'broadcast_shifts',
  'contact_request',
  'invitation',
  'booking_bid',
  'booking_answer',
  'deal_version',
  'contract',
] as const;
export const conversationCardKindSchema = z.enum(CONVERSATION_CARD_KINDS);
export type ConversationCardKind = z.infer<typeof conversationCardKindSchema>;

const cardValue = z.union([z.string(), z.number(), z.boolean(), z.null()]);

export const cardShiftSchema = z.object({
  title: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
  place: z.string().nullable(),
});
export type CardShift = z.infer<typeof cardShiftSchema>;

export const broadcastShiftsCardSchema = z.object({
  card: z.literal('broadcast_shifts'),
  params: z.object({
    /** Festivalens tidssone: vaktene leses der de skjer, ikke der leseren sitter. */
    timezone: z.string(),
    shifts: z.array(cardShiftSchema).max(500),
  }),
});

/**
 * Kontaktforespørselen og invitasjonen har en åpen form her: banene som eier dem (B-kon, B-del)
 * bestemmer feltene. Kortet viser `title` og `text` når de finnes, og tegner knappene banen gir.
 */
export const openCardSchema = z.object({
  card: z.enum(['contact_request', 'invitation']),
  params: z.record(z.string(), cardValue),
});
export type OpenCard = z.infer<typeof openCardSchema>;

/**
 * KONTAKTFORESPØRSELEN SOM KORT (15, 257.11, runde D «D-mel»). Feltene i `params` på et
 * `contact_request`-kort. Kortet er bare visningen: svaret går gjennom `person_connections`
 * (B-kon) eller den gamle organisasjonskoblingen, og knappene leser om forespørselen fortsatt
 * venter.
 *
 * - `source`: `connection` (den personlige koblingen, 0934) eller `person_link` (de gamle
 *   forespørslene om organisasjonskobling, sak 216, 105).
 * - `requestId`: raden svaret går til.
 * - `requestedByPersonId`: den som spør (bare for `connection`; den gamle lista har ikke personen).
 */
export const CONTACT_REQUEST_SOURCES = ['connection', 'person_link'] as const;
export const contactRequestCardParamsSchema = z.object({
  source: z.enum(CONTACT_REQUEST_SOURCES),
  requestId: z.uuid(),
  requestedByPersonId: z.uuid().nullable(),
  requestedByName: z.string(),
  organizationName: z.string().nullable(),
  objectLabel: z.string().nullable(),
  role: z.string().nullable(),
  /** Den gamle forespørselen: bandet eller ledelsen til artisten. */
  artistName: z.string().nullable(),
  linkKind: z.enum(['band', 'management']).nullable(),
  requestedAt: z.string(),
  /**
   * 0970 (259.11): samtalen er felles for begge. Målet slik den som spurte skrev det (e-posten, eller
   * navnet på en person hen kjente), for hens side av kortet — aldri personens eget navn (257.10).
   */
  targetLabel: z.string().nullable().optional(),
  targetEmail: z.string().nullable().optional(),
});
export type ContactRequestCardParams = z.infer<typeof contactRequestCardParamsSchema>;

/** Feltene i et `contact_request`-kort, eller null når kortet er noe annet eller ufullstendig. */
export function contactRequestParamsOf(card: ConversationCard): ContactRequestCardParams | null {
  if (card.card !== 'contact_request') return null;
  const parsed = contactRequestCardParamsSchema.safeParse(card.params);
  return parsed.success ? parsed.data : null;
}

/**
 * ET BUD ELLER EN FORESPØRSEL I BOOKINGTRÅDEN (257.16). Et øyeblikksbilde: det som ble sendt, ikke
 * det budet står som i dag — samtalen er historien, sakskortet øverst er nået.
 *
 * - `kind`: `interest` (forespørsel uten beløp), `bid`, `offer` (som `BOOKING_BID_KINDS`), eller
 *   `counter` — et nytt tall etter byråets motbud.
 * - `date`: dagen det gjelder (YYYY-MM-DD), når det finnes en.
 */
export const bookingBidCardSchema = z.object({
  card: z.literal('booking_bid'),
  params: z.object({
    kind: z.enum(['interest', 'bid', 'offer', 'counter']),
    amountMinor: z.number().int().nullable(),
    currency: z.string(),
    date: z.string().nullable(),
    artist: z.string().nullable(),
    place: z.string().nullable(),
    /**
     * 258.12: beløpet er tatt ut av svaret fordi leseren mangler «Se priser og honorar»
     * (`booking_amount:read`). Settes bare av API-et når det leser kortet ut, aldri lagret.
     */
    amountHidden: z.boolean().optional(),
  }),
});
export type BookingBidCard = z.infer<typeof bookingBidCardSchema>;

/**
 * SVARET (257.16): byråets svar på en forespørsel (ledig, opptatt, hold) eller et bud (godta,
 * motbud, avslå), og arrangørens «godta motbudet». Beløpet er det som gjelder etter svaret.
 */
export const bookingAnswerCardSchema = z.object({
  card: z.literal('booking_answer'),
  params: z.object({
    answer: z.enum(['accept', 'counter', 'decline', 'hold', 'free', 'busy', 'unknown']),
    amountMinor: z.number().int().nullable(),
    currency: z.string(),
    date: z.string().nullable(),
    holdUntil: z.string().nullable(),
    /** 258.12: som på budkortet. */
    amountHidden: z.boolean().optional(),
  }),
});
export type BookingAnswerCard = z.infer<typeof bookingAnswerCardSchema>;


/**
 * EN VERSJON AV AVTALEARKET (C3, 275): hvem som endret hva, og om den ble godtatt, avslått eller
 * trukket. Kortet åpner arket i sideskuffen med versjonshistorikk og diff. Beløpet skjules for den
 * som ikke ser priser, som på budkortet (258.12).
 */
export const dealVersionCardSchema = z.object({
  card: z.literal('deal_version'),
  params: z.object({
    dealSheetId: z.uuid(),
    versionId: z.uuid(),
    versionNumber: z.number().int(),
    /* `amended` (A8): den godtatte avtalen er endret før signering og gjelder med en gang. */
    event: z.enum(['proposed', 'accepted', 'declined', 'withdrawn', 'amended']),
    authorSide: z.enum(['owner', 'counterparty']),
    /** Organisasjonen som skrev versjonen (eller som godtok/avslo/trakk). */
    actorName: z.string().nullable(),
    changedKeys: z.array(z.string()).max(40),
    amountMinor: z.number().int().nullable(),
    currency: z.string().nullable(),
    date: z.string().nullable(),
    artist: z.string().nullable(),
    mode: z.enum(['sign_now', 'sign_later']).nullable(),
    amountHidden: z.boolean().optional(),
  }),
});
export type DealVersionCard = z.infer<typeof dealVersionCardSchema>;

/**
 * KONTRAKTEN I SAMTALEN (C5 punkt 4, C7): «[org] har signert kontrakt TV-…» med knappen «Åpne og
 * signer». Skrevet av `app._contract_post_card` (1014), aldri av en klient.
 */
export const contractCardSchema = z.object({
  card: z.literal('contract'),
  params: z.object({
    contractId: z.uuid(),
    versionId: z.uuid(),
    number: z.string(),
    label: z.string(),
    action: z.string(),
    actorOrganizationName: z.string().nullable(),
    actorName: z.string().nullable(),
    comment: z.string().nullable().optional(),
  }),
});
export type ContractCard = z.infer<typeof contractCardSchema>;

export const conversationCardSchema = z.discriminatedUnion('card', [
  broadcastShiftsCardSchema,
  openCardSchema,
  bookingBidCardSchema,
  bookingAnswerCardSchema,
  dealVersionCardSchema,
  contractCardSchema,
]);
export type ConversationCard = z.infer<typeof conversationCardSchema>;

/** Kortet i kroppen, eller null når kroppen ikke er et kort denne klienten kjenner. */
export function parseConversationCard(body: string): ConversationCard | null {
  try {
    const parsed = conversationCardSchema.safeParse(JSON.parse(body));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
